import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { AppConfig } from '®shared/config';
import { RedirectionService } from '®shared/redirection';

import { AnonymousAuthService } from './anonymous-auth.service';
import { AuthProvider } from './auth-provider';
import { AuthenticationFailedException } from './authentication-failed-exception';
import { PersonaAuthService } from './persona-auth.service';
import { User } from './user';
import { UserScopesService } from './user-scopes.service';

export enum AuthModeType {
  ANONYMOUS = 'anonymous',
  PERSONA = 'persona',
}
export type AuthMode = AuthModeType | undefined;

@Injectable()
export class AuthService extends AuthProvider<User> {
  private isAnonymousAllowed: boolean;

  private delegatedAuthProvider: AuthProvider<User>;

  constructor(
    private anonymousAuthService: AnonymousAuthService,
    appConfig: AppConfig,
    private personaAuthService: PersonaAuthService,
    redirectionService: RedirectionService,
    userScopesService: UserScopesService,
  ) {
    super(appConfig, redirectionService, userScopesService);

    this.isAnonymousAllowed = appConfig.tenant.anonymousAccessToken
      ? true
      : false;
    if (!this.isAnonymousAllowed) {
      this.delegatedAuthProvider = this.personaAuthService;
    }
  }

  getAuthMode(): AuthMode {
    switch (this.delegatedAuthProvider) {
      case this.anonymousAuthService: {
        return AuthModeType.ANONYMOUS;
      }
      case this.personaAuthService: {
        return AuthModeType.PERSONA;
      }
      default: {
        return undefined;
      }
    }
  }

  /**
   * Get the current authentication token.
   *
   * @return {Observable<User>} The user, once the current token has been retrieved
   */
  getCurrentToken(): Observable<User> {
    // use the already-set delegate
    if (this.delegatedAuthProvider) {
      return this.delegatedAuthProvider.getCurrentToken();
    }

    // if anonymous is not permitted, we must use Persona
    if (!this.isAnonymousAllowed || this.isInExplicitLoginMode()) {
      this.delegatedAuthProvider = this.personaAuthService;
      return this.personaAuthService.getCurrentToken();
    }

    // use Persona...
    return this.personaAuthService.getCurrentToken().pipe(
      tap(() => {
        this.delegatedAuthProvider = this.personaAuthService;
      }),
      catchError(error => {
        if (!(error instanceof AuthenticationFailedException)) {
          throw error;
        }

        // ... but fall back to anonymous authentication
        return this.anonymousAuthService.getCurrentToken().pipe(
          tap(() => {
            this.delegatedAuthProvider = this.anonymousAuthService;
          }),
        );
      }),
    );
  }

  /**
   * Refresh the current authentication token.
   *
   * @return {Observable<User>} The user, once the token has been refreshed
   */
  refreshToken(): Observable<User> {
    const provider = this.delegatedAuthProvider
      ? this.delegatedAuthProvider
      : this.personaAuthService;
    return provider.refreshToken();
  }

  /**
   * Obtain a new token for the user
   *
   * @param  {?string}          returnUrl an optional return URL, for redirection
   * @return {Observable<User>}           The user, once a new token is obtained
   */
  obtainNewToken(returnUrl?: string): Observable<User> {
    const provider = this.delegatedAuthProvider
      ? this.delegatedAuthProvider
      : this.personaAuthService;
    return provider.obtainNewToken(returnUrl);
  }

  /**
   * The query parameters `login` or `talis_login` indicate that the user has explicitly
   * indicated that they wish to log in.
   *
   * @return {boolean} `true` when `login` or `talis_login` is specified; `false` otherwise
   */
  isInExplicitLoginMode(): boolean {
    const uri = this.getCurrentUri();
    const query = uri.query(true);
    return query['login'] || query['talis_login'] ? true : false;
  }

  /**
   * Trigger the login flow. This redirects to the current URL with `login` added
   * to the query parameters, to force login with Persona.
   */
  login(): void {
    const loginUrl = this.getCurrentUri().toString();
    this.personaAuthService.handlePersonaRefreshTokenReauthentication(
      loginUrl,
      false,
    );
  }
}
